import { useState } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { DateTime } from 'luxon'
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@smorgrav/velpr-ui'
import { Loader2, RefreshCw } from 'lucide-react'
import { saveMinAppVersion, useAppVersion } from '@/hooks/useAppVersion'

export const Route = createFileRoute('/maintenance')({
  component: MaintenancePage
})

const formatTime = (iso: string) =>
  DateTime.fromISO(iso).toLocal().toFormat('dd.MM.yyyy HH:mm')

function MaintenancePage() {
  return (
    <div className="mx-auto flex max-w-5xl flex-col gap-6">
      <div>
        <h1 className="text-2xl font-bold">Vedlikehold</h1>
        <p className="text-muted-foreground mt-1 text-sm">
          Drift av applikasjonen på tvers av anlegg.
        </p>
      </div>
      <MinAppVersionCard />
    </div>
  )
}

function MinAppVersionCard() {
  const { data, error, loading, refetch } = useAppVersion()
  const [selected, setSelected] = useState<string>('')
  const [saving, setSaving] = useState(false)
  const [notice, setNotice] = useState<string | null>(null)
  const [saveError, setSaveError] = useState<string | null>(null)

  const releases = data?.releases ?? []
  const sites = data?.sites ?? []
  const latest = releases[0]?.release ?? ''
  // Default to the newest app release once the list is loaded
  const version = selected || latest

  const apply = async (minAppVersion: string, siteIds: 'all' | string[]) => {
    const target =
      siteIds === 'all' ? 'alle anlegg' : siteIds.join(', ')
    const what =
      minAppVersion === ''
        ? `Fjern minste appversjon for ${target}?`
        : `Sett minste appversjon ${minAppVersion} for ${target}?\n\n` +
          'Besøkende med en eldre versjon får beskjed om å laste siden på nytt.'
    if (!window.confirm(what)) return
    setSaving(true)
    setNotice(null)
    setSaveError(null)
    try {
      const result = await saveMinAppVersion(minAppVersion, siteIds)
      setNotice(
        `Oppdatert ${result.updated.length} anlegg` +
          (result.missing.length
            ? ` · fant ikke ${result.missing.join(', ')}`
            : '')
      )
      await refetch()
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : String(err))
    } finally {
      setSaving(false)
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Minste appversjon</CardTitle>
        <CardDescription>
          En besøkende med en eldre versjon av appen enn dette får en dialog som
          ber dem laste siden på nytt, også i faner som allerede er åpne. Settes
          per anlegg i anleggsdokumentet. Kun releaser som har deployet appen kan
          velges, ellers ville alle blitt bedt om å laste på nytt hver gang.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-5">
        {error && <p className="text-destructive text-sm">{error}</p>}

        <div className="flex flex-wrap items-center gap-2">
          <Select
            value={version || undefined}
            onValueChange={setSelected}
            disabled={loading || releases.length === 0}
          >
            <SelectTrigger className="w-72">
              <SelectValue placeholder="Velg release" />
            </SelectTrigger>
            <SelectContent>
              {releases.map((r) => (
                <SelectItem key={r.release} value={r.release}>
                  {r.release} · {formatTime(r.deployedAt)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            disabled={saving || !version}
            onClick={() => apply(version, 'all')}
          >
            Sett på alle anlegg
          </Button>
          <Button
            variant="secondary"
            disabled={saving}
            onClick={() => apply('', 'all')}
          >
            Fjern på alle
          </Button>
          <Button
            variant="ghost"
            size="sm"
            disabled={loading}
            onClick={() => void refetch()}
          >
            {loading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <RefreshCw className="h-4 w-4" />
            )}
          </Button>
        </div>
        {!loading && releases.length === 0 && (
          <p className="text-muted-foreground text-sm">
            Ingen app-deploy er registrert i deploys-samlingen.
          </p>
        )}

        {notice && <p className="text-sm text-green-700">{notice}</p>}
        {saveError && <p className="text-destructive text-sm">{saveError}</p>}

        <div className="overflow-x-auto rounded-md border">
          <table className="w-full text-sm">
            <thead className="bg-muted/40 text-muted-foreground text-xs uppercase">
              <tr>
                <th className="px-3 py-2 text-left">Anlegg</th>
                <th className="px-3 py-2 text-left">Minste appversjon</th>
                <th className="px-3 py-2 text-right" />
              </tr>
            </thead>
            <tbody>
              {sites.map((site) => (
                <tr key={site.siteId} className="border-t">
                  <td className="px-3 py-2">
                    <div className="font-medium">{site.name}</div>
                    <div className="text-muted-foreground text-xs">
                      {site.siteId}
                    </div>
                  </td>
                  <td className="px-3 py-2">
                    {site.minAppVersion ? (
                      <Badge
                        variant={
                          site.minAppVersion === latest
                            ? 'default'
                            : 'secondary'
                        }
                      >
                        {site.minAppVersion}
                      </Badge>
                    ) : (
                      <span className="text-muted-foreground">ingen</span>
                    )}
                  </td>
                  <td className="space-x-2 px-3 py-2 text-right whitespace-nowrap">
                    <Button
                      size="sm"
                      variant="secondary"
                      disabled={
                        saving || !version || site.minAppVersion === version
                      }
                      onClick={() => apply(version, [site.siteId])}
                    >
                      Sett {version}
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      disabled={saving || !site.minAppVersion}
                      onClick={() => apply('', [site.siteId])}
                    >
                      Fjern
                    </Button>
                  </td>
                </tr>
              ))}
              {!loading && sites.length === 0 && (
                <tr>
                  <td
                    colSpan={3}
                    className="text-muted-foreground px-3 py-4 text-center"
                  >
                    Ingen anlegg.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  )
}
